import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, ShieldCheck, ArrowLeft, Lock, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { ensureFonts, FONT_AR, FONT_DISPLAY, setStored } from "../../lib/sevenTheme";

const fireGoogleAdsPurchase = (tid) => {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "conversion", {
      send_to: "AW-18239642780/eSJQCMzv7r4cEJy5q_lD",
      value: 1.0, currency: "AED", transaction_id: tid || "",
    });
  } catch (_) { /* never breaks */ }
};

export default function SuccessSeven() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const csId = params.get("cs") || "";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileErr, setReconcileErr] = useState("");
  const [err, setErr] = useState("");
  const firedRef = useRef(false);

  const firePurchase = (o) => {
    if (firedRef.current) return;
    firedRef.current = true;
    try {
      if (csId && window.ttq?.track) {
        const key = `__seven_purchase_${csId}`;
        if (!window[key]) {
          window[key] = true;
          const usdValue = Math.round((o?.amount || 99) * 0.27 * 100) / 100;
          window.ttq.track("Purchase", {
            contents: [{ content_id: `7nights_${o?.bundle || "complete"}`, content_type: "product", content_name: o?.bundle_label || "7 ليالي بيننا", price: usdValue, quantity: 1 }],
            value: usdValue, currency: "USD",
          }, { event_id: `purchase_${csId}` });
        }
      }
    } catch (_) { /* never breaks */ }
    fireGoogleAdsPurchase(csId || token);
  };

  const manualReconcile = async () => {
    setReconcileErr(""); setReconciling(true);
    try {
      const { data } = await api.post(`/seven/reconcile/${token}`);
      if (data?.status === "paid") {
        const { data: o } = await api.get(`/seven/order/${token}`);
        setOrder(o);
        setStored({ token, bundle: o.bundle, email: o.email, paidAt: o.created_at });
        firePurchase(o);
      } else if (data?.reason) setReconcileErr(data.reason);
      else setReconcileErr("الدفع لا يزال قيد المعالجة. حاول مرة أخرى بعد 10-20 ثانية.");
    } catch (e) {
      setReconcileErr(e?.response?.data?.detail || "تعذّر الاتصال بـ Stripe — حاول مرة أخرى.");
    } finally { setReconciling(false); }
  };

  useEffect(() => {
    ensureFonts();
    document.title = "7 ليالي بيننا — تأكيد الطلب";
    let cancelled = false;
    let attempts = 0;
    let reconciled = false;

    const tryReconcile = async () => {
      if (reconciled) return false;
      reconciled = true;
      try {
        const { data } = await api.post(`/seven/reconcile/${token}`);
        if (data?.status === "paid") return true;
      } catch (_) { /* swallow */ }
      return false;
    };

    const poll = async () => {
      while (!cancelled && attempts < 24) {
        attempts += 1;
        try {
          const { data } = await api.get(`/seven/order/${token}`);
          if (cancelled) return;
          setOrder(data);
          if (data.status === "paid") {
            setLoading(false);
            setStored({ token, bundle: data.bundle, email: data.email, paidAt: data.created_at });
            firePurchase(data);
            return;
          }
        } catch (e) {
          if (cancelled) return;
          if (e?.response?.status === 404) {
            setErr("هذا الرابط غير صالح أو منتهي الصلاحية."); setLoading(false); return;
          }
        }
        if (attempts === 4) {
          const flipped = await tryReconcile();
          if (flipped && !cancelled) continue;
        }
        await new Promise((r) => setTimeout(r, attempts < 4 ? 1500 : 3000));
      }
      if (!cancelled) setLoading(false);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, csId]);

  return (
    <div dir="rtl" lang="ar" className="min-h-screen bg-[#0A0606] text-[#F5EAD3] flex flex-col" style={{ fontFamily: FONT_AR }} data-testid="seven-success-root">
      <nav className="px-5 pt-5 max-w-5xl w-full mx-auto flex items-center justify-between">
        <Link to="/7days" className="flex items-center gap-2">
          <img src="/images/7nights-logo.png" alt="" className="h-9 w-9 object-contain" />
          <div className="text-[14px] font-extrabold text-white" style={{ fontFamily: FONT_DISPLAY }}>7 ليالي بيننا</div>
        </Link>
        <span className="text-[11px] text-[#A7956E] flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#C9A66A]" /> وصول خاص</span>
      </nav>

      <main className="flex-1 px-5 py-12 sm:py-20 flex items-center justify-center">
        <div className="max-w-xl w-full">
          {loading && (
            <div className="text-center py-12" data-testid="seven-success-loading">
              <Loader2 className="w-7 h-7 text-[#C9A66A] animate-spin mx-auto mb-4" />
              <div className="text-[15px] text-white font-semibold">جاري التحقق من الدفع…</div>
              <div className="text-[12.5px] text-[#A7956E] mt-1.5">عادة لا يستغرق سوى بضع ثوانٍ.</div>
            </div>
          )}

          {!loading && order?.status === "paid" && (
            <div data-testid="seven-success-card">
              <div className="text-center mb-9">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C9A66A]/[0.15] border border-[#C9A66A]/40 mb-5">
                  <CheckCircle2 className="w-7 h-7 text-[#C9A66A]" strokeWidth={1.8} />
                </div>
                <div className="text-[10.5px] tracking-[0.28em] text-[#C9A66A] font-bold mb-3">تم تأكيد الدفع</div>
                <h1 className="font-bold text-[28px] sm:text-[38px] leading-[1.2] tracking-tight text-white" style={{ fontFamily: FONT_DISPLAY, fontWeight: 800 }}>
                  تجربتك جاهزة.
                </h1>
                <p className="mt-4 text-[14.5px] text-[#CFC3A1] leading-[1.85]">
                  شكرًا لكِ. كل المحتوى مفتوح أمامكِ الآن — اضغطي الزر بالأسفل وابدئي أول ليلة وقتما تريدين.
                </p>
              </div>

              <div className="bg-gradient-to-b from-[#2A1414] to-[#150C0C] border border-[#C9A66A]/45 rounded-2xl p-6 shadow-[0_30px_80px_-25px_rgba(201,166,106,0.45)]">
                <div className="flex items-center gap-4">
                  <span className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#C9A66A] to-[#8A6E3D] flex items-center justify-center shrink-0 font-extrabold text-[#0A0606] text-[18px]">7</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.24em] text-[#A7956E] font-bold">7 ليالي بيننا</div>
                    <div className="text-[15px] font-bold text-white truncate">{order.bundle_label || "النسخة الكاملة"}</div>
                    <div className="text-[11.5px] text-[#A7956E] mt-0.5">{(order.amount || 0).toFixed(0)} {order.currency || "AED"} · دفعة واحدة</div>
                  </div>
                </div>

                <Link
                  to={`/7days/dashboard?t=${encodeURIComponent(token)}`}
                  data-testid="seven-success-open-btn"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] hover:from-[#EBCD93] hover:to-[#D6B477] text-[#0A0606] font-extrabold text-[15px] tracking-wide px-7 py-4 rounded-full transition-all min-h-[54px] shadow-[0_10px_36px_-10px_rgba(201,166,106,0.65)]"
                >
                  افتحي التجربة الآن <ArrowLeft className="w-4 h-4" />
                </Link>

                <div className="mt-5 bg-[#0A0606]/60 border border-[#C9A66A]/22 rounded-xl p-4 text-right">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-[#C9A66A] shrink-0 mt-0.5" />
                    <div className="text-[13px] text-[#E7DDC0] leading-[1.85]">
                      احفظي هذا الرابط في جوالك — هو مفتاح وصولك الخاص للتجربة في أي وقت.
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 text-[12.5px] text-[#A7956E] leading-relaxed text-center">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> أي استفسار؟ راسلينا على support@replyrescue.app</span>
              </div>
            </div>
          )}

          {!loading && order && order.status !== "paid" && (
            <div className="text-center py-10" data-testid="seven-success-pending">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FBBF24]/[0.12] border border-[#FBBF24]/40 mb-5">
                <Loader2 className="w-6 h-6 text-[#FDE68A] animate-spin" />
              </div>
              <h1 className="text-[22px] font-bold text-white">جاري إتمام عملية الدفع…</h1>
              <p className="mt-3 text-[13.5px] text-[#CFC3A1] leading-relaxed max-w-md mx-auto">
                Stripe لا يزال يُنهي تأكيد العملية. سنتحقق منه مباشرة.
              </p>
              <button onClick={manualReconcile} disabled={reconciling} data-testid="seven-success-reconcile" className="mt-6 inline-flex items-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] text-[#0A0606] font-extrabold text-[13.5px] tracking-wide px-6 py-3 rounded-full min-h-[46px] disabled:opacity-60">
                {reconciling ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري التحقق من Stripe…</> : "تحقّقي من Stripe الآن"}
              </button>
              {reconcileErr && <div className="mt-4 text-[12.5px] text-[#F0838F] max-w-md mx-auto">{reconcileErr}</div>}
            </div>
          )}

          {!loading && err && !order && (
            <div className="text-center py-10" data-testid="seven-success-error">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F87171]/[0.12] border border-[#F87171]/40 mb-5">
                <AlertTriangle className="w-6 h-6 text-[#FCA5A5]" />
              </div>
              <h1 className="text-[22px] font-bold text-white">الرابط غير موجود</h1>
              <p className="mt-3 text-[13.5px] text-[#CFC3A1] leading-relaxed max-w-md mx-auto">{err}</p>
              <Link to="/7days" className="mt-6 inline-flex items-center gap-2 bg-gradient-to-b from-[#E2C281] to-[#C9A66A] text-[#0A0606] font-extrabold text-[13.5px] tracking-wide px-6 py-3 rounded-full min-h-[46px]">
                العودة للصفحة الرئيسية <ArrowLeft className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
